import { useState, useCallback, useEffect, useRef } from 'react';

interface UseResizableOptions {
  enabled: boolean;
  size: { width: number; height: number };
  position?: { x: number; y: number };
  onSizeChange: (size: { width: number; height: number }) => void;
  onPositionChange?: (position: { x: number; y: number }) => void;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  direction?: 'right' | 'left' | 'bottom' | 'corner';
}

export function useResizable({
  enabled,
  size,
  position,
  onSizeChange,
  onPositionChange,
  minWidth = 320,
  minHeight = 400,
  maxWidth = 800,
  maxHeight = window.innerHeight * 0.9,
  direction = 'left',
}: UseResizableOptions) {
  const [isResizing, setIsResizing] = useState(false);
  const resizeStart = useRef({ width: 0, height: 0, x: 0, y: 0, posX: 0, posY: 0 });

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) return;

      e.preventDefault();
      e.stopPropagation();

      setIsResizing(true);
      resizeStart.current = {
        width: size.width,
        height: size.height,
        x: e.clientX,
        y: e.clientY,
        posX: position?.x || 0,
        posY: position?.y || 0,
      };
    },
    [enabled, size, position]
  );

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.current.x;
      const deltaY = e.clientY - resizeStart.current.y;

      let newWidth = resizeStart.current.width;
      let newHeight = resizeStart.current.height;
      let newX = resizeStart.current.posX;
      let newY = resizeStart.current.posY;

      if (direction === 'left') {
        newWidth = Math.max(
          minWidth,
          Math.min(maxWidth, resizeStart.current.width - deltaX)
        );
        newX = resizeStart.current.posX + (resizeStart.current.width - newWidth);
      } else if (direction === 'right') {
        newWidth = Math.max(
          minWidth,
          Math.min(maxWidth, resizeStart.current.width + deltaX)
        );
      } else if (direction === 'bottom') {
        newHeight = Math.max(
          minHeight,
          Math.min(maxHeight, resizeStart.current.height + deltaY)
        );
      } else if (direction === 'corner') {
        newWidth = Math.max(
          minWidth,
          Math.min(maxWidth, resizeStart.current.width + deltaX)
        );
        newHeight = Math.max(
          minHeight,
          Math.min(maxHeight, resizeStart.current.height + deltaY)
        );
      }

      onSizeChange({ width: newWidth, height: newHeight });
      
      if (onPositionChange && (direction === 'left')) {
        onPositionChange({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, onSizeChange, onPositionChange, minWidth, minHeight, maxWidth, maxHeight, direction]);

  return { isResizing, handleResizeStart };
}
